import { Action, createReducer, on } from '@ngrx/store';
import {
  isVerdictComputeError,
  verdictBelongsTo,
} from 'src/app/shared/services/virtuals-verdict';
import * as UserDetailsActions from './user-details.actions';
import {
  initialUserDetailsState,
  IUserDetailsState,
  IVirtualsVerdict,
} from './user-details.state';

/**
 * The server's virtuals verdict, normalised off the wire.
 *
 * Fail-closed by construction: only an explicit boolean `true` reads as
 * visible ('true', 1, null, a missing object — all hidden), `reason` is kept
 * only when it is a string, and a `checkedAt` that is not a positive finite
 * number reads as 0, i.e. "never checked", which every TTL check treats as
 * stale. Pure and exported so the reducer spec can drive it directly.
 */
export function normaliseVirtualsVerdict(raw: unknown): IVirtualsVerdict {
  const verdict: any = raw && typeof raw === 'object' ? raw : null;
  const checkedAt = verdict ? verdict.checkedAt : 0;
  return {
    visible: !!verdict && verdict.visible === true,
    reason: verdict && typeof verdict.reason === 'string' ? verdict.reason : null,
    checkedAt:
      typeof checkedAt === 'number' && Number.isFinite(checkedAt) && checkedAt > 0
        ? checkedAt
        : 0,
  };
}

/**
 * The verdict a setUserDetailsData stores: the payload's own, normalised —
 * with ONE exception. A compute ERROR (the backend could not decide, design
 * D4: a Redis stall past its 1.5 s timeout, a database hiccup) never
 * replaces a DECISION the store already holds for the SAME account. That
 * decision is kept, with `checkedAt: 0`, so it reads as stale and the TTL
 * asks the server again on the next trigger (the guard's entry, the tab
 * coming back into view). Without this, one transient failure would hide the
 * VIRTUEL entry and bounce /virtuals deep links for the whole TTL, and
 * nothing would ask again before the player hid and re-showed the tab — the
 * very symptom the move off /catalog removed.
 *
 * Everything else is re-derived from the payload: a different account (or a
 * cleared store, fbId '') inherits nothing, an ERROR with no decision behind
 * it is stored as the hidden answer it is, and a payload without the field
 * (an older backend) reads as hidden, not as whatever the last minute said.
 * Pure and exported so the reducer spec can drive it directly.
 */
export function verdictToStore(
  previous: IUserDetailsState | undefined,
  data: any
): IVirtualsVerdict {
  const incoming = normaliseVirtualsVerdict(data ? data.virtuals : undefined);
  const prior = previous && previous.virtuals;
  // The account the state will carry AFTER this set (setUserDetailsData
  // spreads the payload over the state, so a payload without fbId keeps the
  // stored one) — the kept decision must belong to THAT account, never to a
  // previous owner an fbId-less ERROR payload would otherwise displace.
  const nextFbId =
    data && data.fbId !== undefined ? data.fbId : previous ? previous.fbId : null;
  if (
    isVerdictComputeError(incoming) &&
    prior &&
    verdictBelongsTo({ fbId: previous.fbId }, nextFbId) &&
    !isVerdictComputeError(prior)
  ) {
    return { ...normaliseVirtualsVerdict(prior), checkedAt: 0 };
  }
  return incoming;
}

const userDetailsReducerInternal = createReducer(
  initialUserDetailsState,
  // Every dispatcher passes a FULL GET /user/getUserAccount payload; the
  // verdict that goes with it is verdictToStore's (the payload's own, unless
  // it is an ERROR standing in for a decision this account already has).
  on(UserDetailsActions.setUserDetailsData, (state, { data }) => ({
    ...state,
    ...data,
    virtuals: verdictToStore(state, data),
  })),
  on(UserDetailsActions.clearUserDetailsData, (state) => ({
    ...state,
    ...initialUserDetailsState,
  })),
  // Balance writes (the Supabase feed, the lobby's balance nudge) carry no
  // verdict and leave the stored one exactly as it is, by reference.
  on(UserDetailsActions.setBalances, (state, { balance, availableWithdrawalAmount }) => ({
    ...state,
    balance: balance || 0,
    availableWithdrawalAmount: availableWithdrawalAmount || 0,
  })),
);

export function userDetailsReducer(
  state: IUserDetailsState | undefined,
  action: Action
): IUserDetailsState {
  return userDetailsReducerInternal(state, action);
}
